import React, { useState, useEffect } from "react";
import { createDonation, fetchAllDonors, createBulkDonations, fetchAllShipments } from "../../services/DonationService";
import { fetchAllCampaigns } from "../../services/CampaignService";
import Papa from "papaparse";
import Select from "react-select";
import { FaArrowLeft } from "react-icons/fa";
import { Button } from '../../components/Button';
import { getReactSelectStyles } from "../../utils/reactSelectStyles";

const initialDonation = {
    donor: "",
    campaign: "",
    shipment: "",
    amount: "",
    item: "Bars",
    unitCost: "2.00",
    quantity: "0",
};

const csvHeaders = [
    ["Amount", "Campaign", "Donor Email", "Donor Last Name", "Donor First Name", "Name"],
    ["50", "FOOD for Pom Pom", "anna.tang@sasi.io", "Name", "Surname", "Name Surname"],
    ["100", "FOOD for Pom Pom", "hsiaohan.tang@gmail.com", "", "", "Bob Bob"],
];

const AddDonationModal: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
    const [showManual, setShowManual] = useState(false);
    const [showBulk, setShowBulk] = useState(false);
    const [donation, setDonation] = useState(initialDonation);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [campaigns, setCampaigns] = useState<any[]>([]);
    const [donors, setDonors] = useState<any[]>([]);
    const [shipments, setShipments] = useState<any[]>([]);
    const [bulkFile, setBulkFile] = useState<File | null>(null);
    const [bulkStep, setBulkStep] = useState<"upload" | "review">("upload");
    const [bulkRows, setBulkRows] = useState<any[]>([]);

    useEffect(() => {
        if ((showManual || showBulk) && open) {
            fetchAllCampaigns("true").then(res => {
                if (res && res.data) setCampaigns(res.data);
            });
            fetchAllDonors().then(res => {
                if (res && res.data) setDonors(res.data);
            });
            fetchAllShipments().then(res => {
                if (res && res.data) setShipments(res.data);
            });
        }
    }, [showManual, showBulk, open]);

    const handleManualClick = () => {
        setShowManual(true);
        setShowBulk(false);
        setDonation(initialDonation);
        setError(null);
    };

    const handleBulkClick = () => {
        setShowBulk(true);
        setShowManual(false);
        setError(null);
        setBulkStep("upload");
        setBulkRows([]);
        setBulkFile(null);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setDonation(prev => {
            let updated = { ...prev, [name]: value };
            if (name === "amount" || name === "unitCost") {
                const amount = name === "amount" ? value : prev.amount;
                const unitCost = name === "unitCost" ? value : prev.unitCost;
                const qty = parseFloat(unitCost) > 0 ? (parseFloat(amount) / parseFloat(unitCost)) : 0;
                updated.quantity = isNaN(qty) ? "0" : qty.toFixed(0);
            }
            return updated;
        });
    };

    const generateDonationPayload = (donation: any) => {
        const donationPayload: any = {
            dontation: { connect: donation.donor ? { id: parseInt(donation.donor) } : {name: donation.name} },
            campaign: { connect: { id: parseInt(donation.campaign) } },
            amount: parseFloat(donation.amount),
            unitCost: parseFloat(donation.unitCost),
            numberOfUnits: parseInt(donation.quantity, 0),
            donationDate: new Date().toISOString(),
            donationId: `${Math.random().toString().slice(2,8)}`, // Adding random value temporarily
            label: `${Math.random().toString().slice(2,8)}`, // FIXME: Replace with actual logic to generate or fetch label,
            stripeId: 'na', // FIXME: Replace with actual Stripe ID if applicabl
            languageCode: "EN",
        };

        if (donation.shipment) {
            donationPayload.ShipmentsOnDonations = { "connect": [{ "id": parseInt(donation.shipment) }] };
        }

        return donationPayload;
    };

    // Confirm bulk upload (implement actual upload logic as needed)
    const handleBulkConfirm = async () => {
        setLoading(true);
        setError(null);
        try {
            // Map bulkRows to your backend's expected format
            const payload = bulkRows.map(row => (generateDonationPayload(row)));
            await createBulkDonations(payload);
            setShowBulk(false);
            setBulkRows([]);
            setBulkFile(null);
            setBulkStep("upload");
            onClose();
        } catch (err: any) {
            setError(err.message || "Bulk upload failed");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const donationPayload: any = generateDonationPayload(donation);
            await createDonation(donationPayload);
            setShowManual(false);
            onClose();
        } catch (err: any) {
            setError(err.message || "Failed to save donation");
        } finally {
            setLoading(false);
        }
    };

    const handleBulkClose = () => {
        setShowBulk(false);
        setBulkFile(null);
    };

    const handleDownloadExample = () => {
        const csv = Papa.unparse(csvHeaders);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "donation_upload_example.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleBulkFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setBulkFile(e.target.files[0]);
        }
    };

    // Drag & drop support
    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            setBulkFile(e.dataTransfer.files[0]);
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    };

    // Parse CSV and go to review step
    const handleBulkNext = () => {
        if (!bulkFile) return;
        Papa.parse(bulkFile, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                // Map to expected fields
                const rows = results.data.map((row: any) => ({
                    name: row["Name"] || "",
                    amount: row["Amount"] || "",
                    campaign: "",
                }));
                setBulkRows(rows);
                setBulkStep("review");
            },
        });
    };

    // Remove a row
    const handleRemoveBulkRow = (idx: number) => {
        setBulkRows(rows => rows.filter((_, i) => i !== idx));
    };

    // Change campaign for a row
    const handleBulkCampaignChange = (idx: number, value: string) => {
        setBulkRows(rows =>
            rows.map((row, i) =>
                i === idx ? { ...row, campaign: value } : row
            )
        );
    };

    if (!open) return null;

    // Manual donation form
    if (showManual) {
        return (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-white/50 dark:border-white/10 shadow-lg w-full max-w-3xl">
                    <div className="flex justify-between items-center border-b dark:border-white/10 px-8 py-6">
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">New donation</h2>
                        <Button
                            variant="tertiary"
                            size="sm"
                            className="text-3xl"
                            onClick={() => setShowManual(false)}
                            aria-label="Close"
                            disabled={loading}
                        >
                            &times;
                        </Button>
                    </div>
                    <form className="px-8 py-6" onSubmit={handleSubmit}>
                        <div className="mb-4">
                            <label className="block text-gray-700 dark:text-gray-300 mb-1">Donor</label>
                            <Select
                                options={donors.map((d: any) => ({
                                    value: d.id,
                                    label: d.user?.name || d.supplierCode || d.id
                                }))}
                                value={
                                    donors.find((d: any) => d.id === donation.donor)
                                        ? {
                                            value: donation.donor,
                                            label: donors.find((d: any) => d.id === donation.donor)?.user?.name ||
                                                donors.find((d: any) => d.id === donation.donor)?.supplierCode ||
                                                donors.find((d: any) => d.id === donation.donor)?.id
                                        }
                                        : null
                                }
                                onChange={(option: any) =>
                                    setDonation(prev => ({
                                        ...prev,
                                        donor: option ? option.value : ""
                                    }))
                                }
                                isClearable
                                placeholder="Select donor..."
                                styles={getReactSelectStyles()}
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-gray-700 dark:text-gray-300 mb-1">Campaign</label>
                            <select
                                name="campaign"
                                value={donation.campaign}
                                onChange={handleChange}
                                className="border rounded px-3 py-2 w-full dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
                                required
                            >
                                <option value="">Select campaign</option>
                                {campaigns.map((c: any) => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="mb-4 grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-gray-700 dark:text-gray-300 mb-1">Shipment</label>
                                <select
                                    name="shipment"
                                    value={donation.shipment}
                                    onChange={handleChange}
                                    className="border rounded px-3 py-2 w-full dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
                                >
                                    <option value="">Select shipment</option>
                                    {shipments.map((s: any) => (
                                        <option key={s.id} value={s.id}>{s.name || `Shipment ${s.id}`}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-gray-700 dark:text-gray-300 mb-1">Amount (in US Dollars)</label>
                                <div className="flex items-center border rounded px-2 py-1 w-full bg-white dark:bg-slate-800 dark:border-white/10">
                                    <span className="text-gray-500 dark:text-gray-400 mr-2">$</span>
                                    <input
                                        type="number"
                                        name="amount"
                                        className="flex-1 outline-none bg-transparent dark:text-gray-100"
                                        value={donation.amount}
                                        onChange={handleChange}
                                        min="0"
                                        step="0.01"
                                        placeholder="Enter amount"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="mb-4 grid grid-cols-3 gap-4">
                            <div>
                                <label className="block text-gray-700 dark:text-gray-300 mb-1">Item</label>
                                <select
                                    name="item"
                                    value={donation.item}
                                    onChange={handleChange}
                                    className="border rounded px-3 py-2 w-full dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
                                    disabled
                                >
                                    <option value="Bars">Bars</option>
                                    <option value="Boxes">Boxes</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-gray-700 dark:text-gray-300 mb-1">Unit cost</label>
                                <input
                                    name="unitCost"
                                    value={donation.unitCost}
                                    onChange={handleChange}
                                    className="border rounded px-3 py-2 w-full dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-gray-700 dark:text-gray-300 mb-1">Quantity</label>
                                <input
                                    name="quantity"
                                    value={donation.quantity}
                                    onChange={handleChange}
                                    className="border rounded px-3 py-2 w-full dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
                                    disabled
                                />
                            </div>
                        </div>
                        {error && <div className="text-red-600 mb-2">{error}</div>}
                        <div className="flex justify-end gap-2 mt-8 border-t dark:border-white/10 pt-6">
                            <Button
                                variant="secondary"
                                size="md"
                                type="button"
                                onClick={() => setShowManual(false)}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                size="md"
                                type="submit"
                                disabled={loading}
                            >
                                {loading ? "Saving..." : "Confirm"}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    // Bulk upload modal
    if (showBulk && open) {
        // Step 1: Upload
        if (bulkStep === "upload") {
            return (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-white/50 dark:border-white/10 shadow-lg w-full max-w-md">
                        <div className="flex justify-between items-center border-b dark:border-white/10 px-8 py-6">
                            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Upload donors</h2>
                            <Button
                                variant="tertiary"
                                size="sm"
                                onClick={handleBulkClose}
                                aria-label="Close"
                                className="text-3xl"
                            >
                                &times;
                            </Button>
                        </div>
                        <div className="px-8 py-6">
                            <Button
                                variant="tertiary"
                                size="sm"
                                className="mb-4 block"
                                type="button"
                                onClick={handleDownloadExample}
                            >
                                Download CSV file example
                            </Button>
                            <div
                                className="border-2 border-dashed border-gray-300 dark:border-white/10 rounded-lg flex flex-col items-center justify-center py-10 mb-6 cursor-pointer"
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onClick={() => document.getElementById("bulk-upload-input")?.click()}
                            >
                                <input
                                    id="bulk-upload-input"
                                    type="file"
                                    accept=".csv"
                                    className="hidden"
                                    onChange={handleBulkFileChange}
                                />
                                <svg width="48" height="48" fill="none" stroke="#7b8794" strokeWidth="2" viewBox="0 0 24 24">
                                    <rect x="4" y="4" width="16" height="16" rx="2" stroke="#7b8794" strokeWidth="2" fill="none"/>
                                    <path d="M8 12h8M12 8v8" stroke="#7b8794" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                                <div className="mt-4 text-gray-500 dark:text-gray-400 text-center">
                                    {bulkFile
                                        ? <span>{bulkFile.name}</span>
                                        : <>Select file to upload<br /><span className="text-xs">or Drag and Drop here</span></>
                                    }
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 mt-4">
                                <Button
                                    variant="secondary"
                                    size="md"
                                    type="button"
                                    onClick={handleBulkClose}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    size="md"
                                    type="button"
                                    disabled={!bulkFile}
                                    onClick={handleBulkNext}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // Step 2: Review
        if (bulkStep === "review") {
            return (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-white/50 dark:border-white/10 shadow-lg w-full max-w-2xl">
                        <div className="flex justify-between items-center border-b dark:border-white/10 px-8 py-6">
                            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Upload donors</h2>
                            <Button
                                variant="tertiary"
                                size="sm"
                                className="text-3xl"
                                onClick={handleBulkClose}
                                aria-label="Close"
                            >
                                &times;
                            </Button>
                        </div>
                        <div className="px-8 py-6">
                            <Button
                                variant="tertiary"
                                size="sm"
                                type="button"
                                className="mb-4 flex items-center"
                                onClick={() => setBulkStep("upload")}
                            >
                                <FaArrowLeft className="mr-2" />
                                Back
                            </Button>
                            <div className="max-h-64 overflow-y-auto">
                                <table className="min-w-full mb-8 border border-gray-200 dark:border-white/10 rounded-lg overflow-hidden">
                                    <thead>
                                        <tr className="bg-gray-50 dark:bg-slate-800">
                                            <th className="py-3 px-4 border-b dark:border-white/10 text-left font-medium text-gray-900 dark:text-gray-100" style={{width: "35%"}}>Full name</th>
                                            <th className="py-3 px-4 border-b dark:border-white/10 text-left font-medium text-gray-900 dark:text-gray-100">Amount</th>
                                            <th className="py-3 px-4 border-b dark:border-white/10 text-left font-medium text-gray-900 dark:text-gray-100">Campaign</th>
                                            <th className="py-3 px-4 border-b dark:border-white/10"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bulkRows.map((row, idx) => (
                                            <tr key={idx} className="bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-800">
                                                <td className="py-3 px-4 border-b dark:border-white/10 text-gray-900 dark:text-gray-100" style={{width: "35%"}}>{row.name}</td>
                                                <td className="py-3 px-4 border-b dark:border-white/10 text-gray-900 dark:text-gray-100">${row.amount}</td>
                                                <td className="py-3 px-4 border-b dark:border-white/10">
                                                    <select
                                                        className="border rounded px-3 py-2 w-full text-gray-700 dark:text-gray-100 dark:border-white/10 dark:bg-slate-800"
                                                        value={row.campaign}
                                                        onChange={e => handleBulkCampaignChange(idx, e.target.value)}
                                                    >
                                                        <option value="">Select campaign</option>
                                                        {campaigns.map((c: any) => (
                                                            <option key={c.id} value={c.id}>{c.name}</option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td className="py-3 px-4 border-b dark:border-white/10 text-center">
                                                    <Button
                                                        variant="tertiary"
                                                        size="sm"
                                                        type="button"
                                                        className="text-gray-400 hover:text-red-600 text-xl"
                                                        onClick={() => handleRemoveBulkRow(idx)}
                                                    >
                                                        &times;
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="flex justify-end gap-4 mt-6">
                                <Button
                                    variant="secondary"
                                    size="md"
                                    onClick={handleBulkClose}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={handleBulkConfirm}
                                    disabled={bulkRows.some(row => !row.campaign) || loading}
                                >
                                    {loading ? "Saving..." : "Confirm"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
    }

    // Main modal (Manual/Bulk upload selection)
    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-white/50 dark:border-white/10 shadow-lg w-full max-w-2xl">
                <div className="flex justify-between items-center border-b dark:border-white/10 px-8 py-6">
                    <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Add donation</h2>
                    <Button
                        variant="tertiary"
                        size="sm"
                        onClick={onClose}
                        aria-label="Close"
                        className="text-3xl"
                    >
                        &times;
                    </Button>
                </div>
                <div className="flex gap-8 justify-center px-8 py-10">
                    <div
                        className="flex flex-col items-center flex-1 border dark:border-white/10 rounded-lg p-8 hover:shadow cursor-pointer transition"
                        onClick={handleManualClick}
                    >
                        <svg width="48" height="48" fill="none" stroke="#7b8794" strokeWidth="2" viewBox="0 0 24 24">
                            <rect x="4" y="4" width="16" height="16" rx="2" stroke="#7b8794" strokeWidth="2" fill="none"/>
                            <path d="M8 12h8M12 8v8" stroke="#7b8794" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        <div className="font-semibold mt-4 text-lg text-gray-900 dark:text-gray-100">Manual</div>
                        <div className="text-gray-500 dark:text-gray-400 text-center mt-2 text-sm">
                            Manually capture a donation made to E2G FOOD
                        </div>
                    </div>
                    <div
                        className="flex flex-col items-center flex-1 border dark:border-white/10 rounded-lg p-8 hover:shadow cursor-pointer transition"
                        onClick={handleBulkClick}
                    >
                        <svg width="48" height="48" fill="none" stroke="#7b8794" strokeWidth="2" viewBox="0 0 24 24">
                            <rect x="6" y="3" width="12" height="18" rx="2" stroke="#7b8794" strokeWidth="2" fill="none"/>
                            <path d="M12 8v4M12 16h.01" stroke="#7b8794" strokeWidth="2" strokeLinecap="round"/>
                            <path d="M9 12l3 3 3-3" stroke="#7b8794" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <div className="font-semibold mt-4 text-lg text-gray-900 dark:text-gray-100">Bulk upload</div>
                        <div className="text-gray-500 dark:text-gray-400 text-center mt-2 text-sm">
                            Upload a formatted CSV file to import bulk donations
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddDonationModal;
